import { createContext, useContext, useEffect, useState } from "react";
import { apiGet } from "../services/genesisApi/httpsMethods";
import type {
  Sponsor,
  Source,
  Country,
  Sector,
  Status,
  User,
  LoanType,
  RiskClass,
  SecurityType,
  TransactionType,
  Contact,
  Deal,
  KillReason,
  LostToLenderReason,
  CapitalSource,
} from "../types/deal";

const GlobalUseReferenceContext = createContext<any>(null);

export const GlobalReferenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [condensedSponsors, setCondensedSponsors] = useState<Sponsor[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [status, setStatus] = useState<Status[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [riskClasses, setRiskClasses] = useState<RiskClass[]>([]);
  const [securityTypes, setSecurityTypes] = useState<SecurityType[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<TransactionType[]>(
    [],
  );
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [quarters, setQuarters] = useState<string[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [killReasons, setKillReasons] = useState<KillReason[]>([]);
  const [lostToLenderReasons, setLostToLenderReasons] = useState<
    LostToLenderReason[]
  >([]);
  const [capitalSources, setCapitalSources] = useState<CapitalSource[]>([]);

  useEffect(() => {
    setCurrencies(["GBP", "EUR", "DKK", "AUD", "CHF", "SEK"]);
    setQuarters(["Q1", "Q2", "Q3", "Q4"]);
    Promise.all([
      apiGet("sponsors").then((response) =>
        response.success
          ? setSponsors(response.data)
          : console.error("Failed to fetch sponsors"),
      ),
      apiGet("sponsors", { condensed: true }).then((response) =>
        response.success
          ? setCondensedSponsors(response.data)
          : console.error("Failed to fetch condensed sponsors"),
      ),
      apiGet("sources").then((response) =>
        response.success
          ? setSources(response.data)
          : console.error("Failed to fetch sources"),
      ),
      apiGet("contacts").then((response) =>
        response.success
          ? setContacts(response.data)
          : console.error("Failed to fetch contacts"),
      ),
      apiGet("countries").then((response) =>
        response.success
          ? setCountries(response.data)
          : console.error("Failed to fetch countries"),
      ),
      apiGet("sectors").then((response) =>
        response.success
          ? setSectors(response.data)
          : console.error("Failed to fetch sectors"),
      ),
      apiGet("status").then((response) =>
        response.success
          ? setStatus(response.data)
          : console.error("Failed to fetch status"),
      ),
      apiGet<User[]>("users").then((response) => {
        if (!response.success) {
          console.error("Failed to fetch users");
          return;
        }
        const data = Array.isArray(response.data) ? response.data : [];
        setAllUsers(data);
        setUsers(data.filter((user) => user.is_active !== false));
      }),
      apiGet("loan-types").then((response) =>
        response.success
          ? setLoanTypes(response.data)
          : console.error("Failed to fetch loan types"),
      ),
      apiGet("risk-classes").then((response) =>
        response.success
          ? setRiskClasses(response.data)
          : console.error("Failed to fetch risk classes"),
      ),
      apiGet("security-types").then((response) =>
        response.success
          ? setSecurityTypes(response.data)
          : console.error("Failed to fetch security types"),
      ),
      apiGet("transaction-types").then((response) =>
        response.success
          ? setTransactionTypes(response.data)
          : console.error("Failed to fetch transaction types"),
      ),
      apiGet("deals", { condensed: true }).then((response) =>
        response.success
          ? setDeals(response.data)
          : console.error("Failed to fetch deals"),
      ),
      apiGet("kill-reasons").then((response) =>
        response.success
          ? setKillReasons(response.data)
          : console.error("Failed to fetch kill reasons"),
      ),
      apiGet<LostToLenderReason[]>("lost-to-lender-reasons").then(
        (response) => {
          if (response.success && response.data) {
            setLostToLenderReasons(response.data);
          } else {
            console.error("Failed to fetch lost-to-lender reasons");
            setLostToLenderReasons([]);
          }
        },
      ),
      apiGet("capital-sources").then((response) =>
        response.success
          ? setCapitalSources(response.data)
          : console.error("Failed to fetch capital sources"),
      ),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <GlobalUseReferenceContext.Provider
      value={{
        loading,
        sponsors,
        condensedSponsors,
        sources,
        contacts,
        countries,
        sectors,
        status,
        users,
        allUsers,
        loanTypes,
        riskClasses,
        securityTypes,
        transactionTypes,
        currencies,
        quarters,
        deals,
        killReasons,
        lostToLenderReasons,
        capitalSources,
      }}
    >
      {children}
    </GlobalUseReferenceContext.Provider>
  );
};

export const useReferenceLists = () => {
  const context = useContext(GlobalUseReferenceContext);
  if (!context) {
    throw new Error(
      "useReferenceLists must be used within a GlobalReferenceProvider",
    );
  }
  return context;
};
